import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Constants } from "@eagerdog/constants";
import { IScore } from "@eagerdog/interfaces";

import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";

import "./FarmDogResults.scss";

interface IProps {
  result: IScore,
  onChange(result: any): void
}

interface IFarmDogCheck {
  value: string,
  id: string,
  checked: boolean
}

const FarmDogResults: React.FC<IProps> = (props) => {
  const resultTypeOptions:IOption[] = useMemo(() => [
    { id: Constants.score_result_type.qualified, value: "Qualified" },
    { id: Constants.score_result_type.not_qualified, value: "NQ"},
    { id: Constants.score_result_type.absent, value: "Absent" }
  ], []);
  const emptyResultType = { value: "None", id: "None" };
  const passedOption = { value: "Pass", id: "Pass" };
  const trainingOption = { value: "Needs Training", id: "Needs Training" };

  const [loaded, setLoaded] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState<boolean>(false);

  const [resultType, setResultType] = useState<IOption>(
    props.result.farm_dog ? resultTypeOptions.find((r) => r.id === props.result.farm_dog?.result_type) ?? emptyResultType : emptyResultType
  );

  const [dropdownAnswers, setDropdownAnswers] = useState<IOption[]>([]);

  const changeDropdownAnswer = (value: IOption, index: number) => {
    let _answers:IOption[] = [...dropdownAnswers];

    _answers[index] = value;

    setDropdownAnswers(_answers);
  }

  const createAnswerDropdown = (label: string, id: string, index: number, options?: IOption[]) => {
    let _options:IOption[] = [
      passedOption,
      trainingOption
    ];

    return <Dropdown
      label={label}
      id={id}
      options={options ? options : _options}
      value={dropdownAnswers[index]}
      placeholder=""
      onChange={(_e, value: IOption) => { changeDropdownAnswer(value, index); setHasChanged(true); }}
    />
  }

  const getResultForm = () => {
    if (props.result && props.result.dog_class) {
      return <>
        {createAnswerDropdown("Greet Evaluator – Initial Dog Appraisal", "greet_evaluator_initial_dog_appraisal", 0)}
        {createAnswerDropdown("Perform a Walk Pattern around Farm Environment and by Passive Stranger", "perform_walk_pattern_around_farm_environment_and_by_passive_stranger", 1)}
        {createAnswerDropdown("Jump on Hay/Straw Bale", "jump_on_hay_straw_bale", 2)}
        {createAnswerDropdown("Walk by Farm Animal(s)", "walk_by_farm_animal", 3)}
        {createAnswerDropdown("Walk Over or Through Unusual Surfaces", "walk_over_or_through_unusual_surfaces", 4)}
        {createAnswerDropdown("Supervised Separation", "supervised_separation", 5)}
        {createAnswerDropdown("Pass Through a Gate", "pass_through_gate", 6)}
        {createAnswerDropdown("Handler Feeds Livestock", "handler_feeds_livestock", 7)}
        {createAnswerDropdown("Reaction to Another Dog", "reaction_to_another_dog", 8)}
      </>;
    }
  }

  const getOptionValue = (value: boolean | undefined) => {
    return value ? passedOption : trainingOption;
  }

  const checkIfPassed = useCallback(() => {
    if (resultType.id === Constants.score_result_type.absent) {
      return false;
    }
    return !dropdownAnswers.some((opt: IOption) => (opt.value === trainingOption.id));
  }, [dropdownAnswers]);

  useEffect(() => {
    if (resultType.id === Constants.score_result_type.qualified) {
      setDropdownAnswers([
        passedOption,
        passedOption,
        passedOption,
        passedOption,
        passedOption,
        passedOption,
        passedOption,
        passedOption,
        passedOption,
      ]);
    } else {
      setDropdownAnswers([
        trainingOption,
        trainingOption,
        trainingOption,
        trainingOption,
        trainingOption,
        trainingOption,
        trainingOption,
        trainingOption,
        trainingOption,
      ]);
    }
  }, [resultType]);

  useEffect(() => {
    if (!loaded) {
      setDropdownAnswers([
        getOptionValue(props.result.farm_dog?.greet_evaluator_initial_dog_appraisal),
        getOptionValue(props.result.farm_dog?.perform_walk_pattern_around_farm_environment_and_by_passive_stranger),
        getOptionValue(props.result.farm_dog?.jump_on_hay_straw_bale),
        getOptionValue(props.result.farm_dog?.walk_by_farm_animal),
        getOptionValue(props.result.farm_dog?.walk_over_or_through_unusual_surfaces),
        getOptionValue(props.result.farm_dog?.supervised_separation),
        getOptionValue(props.result.farm_dog?.pass_through_gate),
        getOptionValue(props.result.farm_dog?.handler_feeds_livestock),
        getOptionValue(props.result.farm_dog?.reaction_to_another_dog),
      ]);

      setLoaded(true);
    }
  }, [loaded, props.result]);

  useEffect(() => {
    if (hasChanged) {
      const determineDropdownValue = (val: string) => {
        return val === passedOption.id;
      }

      let _changed: any = {
        result_type: resultType.id
      };

      const options = {
        greet_evaluator_initial_dog_appraisal: determineDropdownValue(dropdownAnswers[0].value),
        perform_walk_pattern_around_farm_environment_and_by_passive_stranger: determineDropdownValue(dropdownAnswers[1].value),
        jump_on_hay_straw_bale: determineDropdownValue(dropdownAnswers[2].value),
        walk_by_farm_animal: determineDropdownValue(dropdownAnswers[3].value),
        walk_over_or_through_unusual_surfaces: determineDropdownValue(dropdownAnswers[4].value),
        supervised_separation: determineDropdownValue(dropdownAnswers[5].value),
        pass_through_gate: determineDropdownValue(dropdownAnswers[6].value),
        handler_feeds_livestock: determineDropdownValue(dropdownAnswers[7].value),
        reaction_to_another_dog: determineDropdownValue(dropdownAnswers[8].value),
      }

      if (props.result && props.result.dog_class) {
        _changed = {
          result_type: resultType.id,
          passed: checkIfPassed(),
        };
        if (resultType.id !== Constants.score_result_type.absent) {
          _changed = {
            ..._changed,
            ...options
          };
        }
      }

      props.onChange(_changed);

      setHasChanged(false);
    }
  }, [hasChanged, props, checkIfPassed, dropdownAnswers]);

  return (
    <div className="FarmDogResults">
      {loaded && <div className="resultWrap">
        <Dropdown
          value={resultType}
          label="Result"
          options={resultTypeOptions}
          placeholder=""
          className="resultWrapType"
          onChange={(e: any, value: IOption) => { setResultType(value); setHasChanged(true); }}
        />
        {resultType.id !== Constants.score_result_type.absent && getResultForm()}
      </div>}
    </div>
  );
};

export default FarmDogResults;